import {gql} from '@apollo/client';
import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext, AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {Measurement, MeasurementUnitFragment} from 'constants/MeasurementConstants';
import * as Context from 'Context';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {MeasurementType} from 'samples/MeasurementForm';
import useOnMount from 'ui-library/hooks/useOnMount';

import {
  ConcentrationEditorModalQuery,
  ConcentrationEditorModalQueryVariables,
} from './__generated/ConcentrationEditorModalQuery';
import MeasurementEditorModal, {MeasurementEditorModalSubmitArgs} from './MeasurementEditorModal';

type ContainerOrInaccessible = ConcentrationEditorModalQuery['containers'][number];

type ContainerContentInfo = ExtractByTypename<ContainerOrInaccessible, 'ContainerNode'>;

export const CONCENTRATION_EDITOR_MODAL_QUERY = gql`
  query ConcentrationEditorModalQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          id
          concentration {
            value
            unit {
              ...MeasurementUnit
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${MeasurementUnitFragment.fragment}
`;

interface Props {
  containerIds: Array<string>;
  onRequestSubmit: (args: MeasurementEditorModalSubmitArgs) => void;
  onRequestClose: () => void;
  analyticsMetadata: AnalyticsMetadataForContext;
}

export default function ConcentrationEditorModal(props: Props): JSX.Element {
  const result = useBenchlingQuery<ConcentrationEditorModalQuery, ConcentrationEditorModalQueryVariables>(
    CONCENTRATION_EDITOR_MODAL_QUERY,
    {
      variables: {containerIds: props.containerIds},
      // TODO(BNCH-45906) Nested data changes within the container do not always sync to the cache correctly
      fetchPolicy: 'network-only',
    }
  );
  let accessibleContainers: Array<ContainerContentInfo> = [];
  if (result.type === QueryResultType.SUCCESS) {
    accessibleContainers = result.data.containers.filter(
      (node: ContainerOrInaccessible): node is ContainerContentInfo => !isInaccessible(node)
    );
  }
  const measurements: Array<Measurement> = accessibleContainers.flatMap((container) =>
    container.contents.map((content) => content.concentration)
  );
  const [analyticsMetadata, setAnalyticsMetadata] = useState({
    actionOrigin: ActionOrigin.UI_UNDETERMINED,
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_CONCENTRATION,
  });

  useOnMount(() => {
    if (props.analyticsMetadata.eventGroupId == null) {
      setAnalyticsMetadata((prevAnalyticsMetadataDict) => ({
        ...prevAnalyticsMetadataDict,
        eventGroupId: Analytics.makeAnalyticsEventId(),
      }));
    }
  });

  const onRequestSubmit = ({
    newMeasurement,
    selectedContainerIds,
  }: MeasurementEditorModalSubmitArgs): void => {
    Context.usingAsync({analyticsMetadata}, null, () => {
      props.onRequestSubmit({newMeasurement, selectedContainerIds});
    });
  };

  return (
    <MeasurementEditorModal
      queryResultType={result.type}
      label="concentration"
      labelPlural="concentrations"
      measurements={measurements}
      measurementType={MeasurementType.CONCENTRATION}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onRequestSubmit}
      sampleStatusWarningParams={{
        actionAttempted: 'update_concentration',
        actionOrigin: analyticsMetadata.actionOrigin,
        sampleIds: props.containerIds,
        eventGroupId: analyticsMetadata.eventGroupId ?? Analytics.makeAnalyticsEventId(),
      }}
      itemIds={props.containerIds}
      analyticsMetadata={analyticsMetadata}
    />
  );
}
